/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { AffiliationLoyaltyTier, PriceLookupContext, ProductPrice, ProjectionDomain } from '@msdyn365-commerce/retail-proxy';
import { getActivePricesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

/**
 * Getaddresses Input Action
 */

export interface IITSGetActivePricesInput extends Msdyn365.IActionInput {
    projectDomain: ProjectionDomain | null;
    productIds?: number[];
    activeDate?: Date;
    customerId?: string | null;
    affiliationLoyaltyTiers?: AffiliationLoyaltyTier[] | null;
    includeSimpleDiscountsInContextualPrice?: boolean | null;
    includeVariantPriceRange?: boolean | null;
    includeAttainablePricesAndDiscounts?: boolean | null;
    priceLookupContext?: PriceLookupContext | null;
}
export class GetActivePricesInput implements Msdyn365.IActionInput {
    public projectDomain: ProjectionDomain | null;
    public productIds?: number[];
    public activeDate?: Date;
    public customerId?: string | null;
    public affiliationLoyaltyTiers?: AffiliationLoyaltyTier[] | null;
    public includeSimpleDiscountsInContextualPrice?: boolean | null;
    public includeVariantPriceRange?: boolean | null;
    public includeAttainablePricesAndDiscounts?: boolean | null;
    public priceLookupContext?: PriceLookupContext | null;
    // TODO: Construct the input needed to run the action
    constructor(
        projectDomain: ProjectionDomain | null,
        productIds?: number[],
        activeDate?: Date,
        customerId?: string | null,
        affiliationLoyaltyTiers?: AffiliationLoyaltyTier[] | null,
        includeSimpleDiscountsInContextualPrice?: boolean | null,
        includeVariantPriceRange?: boolean | null,
        includeAttainablePricesAndDiscounts?: boolean | null,
        priceLookupContext?: PriceLookupContext | null
    ) {
        this.projectDomain = projectDomain;
        this.productIds = productIds;
        this.activeDate = activeDate;
        this.customerId = customerId;
        this.affiliationLoyaltyTiers = affiliationLoyaltyTiers;
        this.includeSimpleDiscountsInContextualPrice = includeSimpleDiscountsInContextualPrice;
        this.includeVariantPriceRange = includeVariantPriceRange;
        this.includeAttainablePricesAndDiscounts = includeAttainablePricesAndDiscounts;
        this.priceLookupContext = priceLookupContext;
    }

    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () => `getActivePrices`;
    public getCacheObjectType = () => 'getActivePrices';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

/**
 * TODO: Use this function to call your action and process the results as needed
 */
export async function getActivePricesAction(
    request: IITSGetActivePricesInput,
    ctx: Msdyn365.IActionContext
): Promise<ProductPrice[] | null> {
    if (request) {
        try {
            const result = await getActivePricesAsync(
                { callerContext: ctx },
                request.projectDomain,
                request.productIds,
                request.activeDate,
                request.customerId,
                request.affiliationLoyaltyTiers,
                request.includeSimpleDiscountsInContextualPrice,
                request.includeVariantPriceRange,
                request.includeAttainablePricesAndDiscounts,
                request.priceLookupContext
            ); //calling getActivePricesAsync API
            if (result) {
                return result;
            }
        } catch (error) {
            console.log('Error', error);
        }
    }
    return null;
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<ProductPrice[] | null>>getActivePricesAction,
    // TODO: Give your data action a unique id
    id: `getActivePrices`
    // input: createInput
    // TODO: Uncomment the below line if this is a meant to be a batched data action
    // isBatched: true
});
